// @flow

import { extendObservable, action } from "mobx";
import FilterObject, {
  FilterWithSort,
  ValueSetFilter,
  FilterCriteriaLoader,
  IViewItem,
} from "../Filter";
import type { FilterColumn } from "../Filter";
import { GraphQLQuery } from "../GraphQL";
import Sort from "../Sort";
import SortState from "../SortState";
import { filter } from "fuzzaldrin-plus";

class JobLabelCriterionItem implements IViewItem {
  id: string;
  jobLabel: string;
  selected: boolean;

  constructor(object: Object) {
    this.id = object.id;
    this.jobLabel = object.jobLabel;

    extendObservable(this, {
      selected: object.selected,
    });
  }
}

export default class JobLabelFilter extends FilterCriteriaLoader(
  ValueSetFilter(FilterWithSort)
) {
  constructor(
    store: Object,
    column: FilterColumn,
    filterCriteriaQuery: (FilterColumn) => GraphQLQuery,
    processPayload: (FilterColumn, Object) => ?Array<Object>,
    applyFilter: (FilterColumn, FilterObject) => void,
    applySort: (FilterColumn, Sort) => void,
    removeFilter: (FilterColumn) => void,
    removeSort: (FilterColumn) => void,
    reloadCriteria: boolean = false
  ) {
    super(store, column);

    this.filterCriteriaQuery = filterCriteriaQuery;
    this.processPayload = processPayload;
    this.applyFilter = applyFilter;
    this.applySort = applySort;
    this.removeFilter = removeFilter;
    this.removeSort = removeSort;

    this.sortState = new SortState("JOB_LABEL");

    this.reloadCriteria = reloadCriteria;

    this.viewItemClass = JobLabelCriterionItem;

    // $FlowFixMe: Need to type these properties as invariant
    this.onInstantSearch = action(this.onInstantSearch.bind(this));
    // $FlowFixMe: Need to type these properties as invariant
    this.buildQueryFilter = action(this.buildQueryFilter.bind(this));
  }

  onInstantSearch(value: string) {
    if (super.onInstantSearch) super.onInstantSearch(value);

    if (!this.instantSearchValue) {
      this.viewItems = this.unfilteredViewItems;
      return;
    }

    this.viewItems = filter(this.unfilteredViewItems, value, {
      key: "jobLabel",
      maxResults: 20,
    });
  }

  buildQueryFilter() {
    const labels = this.selectedValues
      .entries()
      .filter((entry) => entry[1] === true)
      .map((entry) => this.criteria.get(entry[0]).jobLabel);

    const jobLabel = this.selectedValue;

    if (!labels.length && (!jobLabel || !jobLabel.trim())) {
      return null;
    }

    let params = [];
    let args = [];
    let vars = {};

    if (jobLabel && jobLabel.trim()) {
      params.push("$jobLabel: String!");
      args.push("jobLabelIContains: $jobLabel");
      vars.jobLabel = jobLabel.trim();
    }

    if (labels.length) {
      params.push("$jobLabels: [JobLabelCriterionInput]!");
      args.push("jobLabels: $jobLabels");
      vars.jobLabels = labels.map((label) => {
        return { jobLabel: label };
      });
    }

    return new FilterObject(params.join(", "), args.join(", "), vars);
  }
}
